import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MODULES_COMPLEMENTAIRES,
  MODULES_SERVICES,
  REGISTRE_MODULES,
  modulesDependantDe,
  type ModuleOptionnel,
} from '@artosera/shared';
import { api } from './client.js';
import { useMutation } from './mutation.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { modeDemonstrationActif } from './demo.js';
import { estApiError } from './errors.js';

export type DisponibiliteModule = 'activable' | 'reserve' | 'non_livre' | 'verrouille' | 'coupe';

export interface ModuleGalerie {
  id: string;
  famille: 'complementaire' | 'service';
  categorie: string | null;
  palier: 'mvp' | 'v1' | 'v2' | 'finale';
  dependances: string[];
  dependants: string[];
  /** Inscrit dans la liste de la galerie. */
  actif: boolean;
  /** Réellement en service : actif, dépendances présentes, pas fermé. */
  effectif: boolean;
  disponibilite: DisponibiliteModule;
  dependancesManquantes: string[];
}

export interface ModulesGalerie {
  revision: number;
  /** `false` : l'état global des fermetures n'a pas pu être relu. */
  interrupteursLisibles: boolean;
  modules: ModuleGalerie[];
}

export interface CorpsBasculeModule {
  module: string;
  actif: boolean;
  revision: number;
}

const cleModules = (galerie: string) => cleMetier(galerie, 'modules-galerie');

const urlModules = (galerie: string) => `/g/${encodeURIComponent(galerie)}/reglages/modules`;

function disponibiliteDeDemonstration(id: ModuleOptionnel): DisponibiliteModule {
  if (REGISTRE_MODULES[id].famille === 'service' || id === 'sso' || id === 'console-groupe')
    return 'reserve';
  if (!REGISTRE_MODULES[id].disponible) return 'non_livre';
  return 'activable';
}

/** La liste vue en démonstration : rien n'est actif, les réservés le restent. */
function modulesDeDemonstration(): ModulesGalerie {
  const page: readonly ModuleOptionnel[] = [...MODULES_COMPLEMENTAIRES, ...MODULES_SERVICES];
  return {
    revision: 0,
    interrupteursLisibles: true,
    modules: page.map((id) => {
      const definition = REGISTRE_MODULES[id];
      return {
        id,
        famille: definition.famille,
        categorie: definition.categorie ?? null,
        palier: definition.palier,
        dependances: [...definition.dependances],
        dependants: modulesDependantDe(id).filter((autre) =>
          (page as readonly string[]).includes(autre),
        ),
        actif: false,
        effectif: false,
        disponibilite: disponibiliteDeDemonstration(id),
        dependancesManquantes: [...definition.dependances],
      };
    }),
  };
}

export function useModulesGalerie(galerie: string, autorise: boolean) {
  return useQuery({
    queryKey: cleModules(galerie),
    queryFn: (contexte): Promise<ModulesGalerie> =>
      modeDemonstrationActif()
        ? Promise.resolve(modulesDeDemonstration())
        : api.get(urlModules(galerie), { galerie, ...appelDeRequete(contexte) }),
    enabled: autorise,
  });
}

export function useBasculerModule(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: ({ module, actif, revision }: CorpsBasculeModule, appel) => {
        if (modeDemonstrationActif()) throw new Error('demo');
        return api.put<ModulesGalerie>(
          `${urlModules(galerie)}/${encodeURIComponent(module)}`,
          { actif, revision },
          { galerie, ...appel },
        );
      },
      onSuccess: (donnees) => {
        client.setQueryData(cleModules(galerie), donnees);
      },
      onError: (erreur) => {
        if (estApiError(erreur) && erreur.status === 409) {
          return client.invalidateQueries({ queryKey: cleModules(galerie) });
        }
      },
    }),
  );
}
